import type { JSX } from 'solid-js';
import type { CardMenuId } from '../../lib/machine-card';

// The card menu's muted 13px icons (#6785), the prototype's `CMIC` set (`prototype.html`
// `:1842-1856`). Decorative: the row's words name it, so every glyph is `aria-hidden`.
// `jobs_at_once` (#6740) has no prototype glyph: three stacked bars, a v2 addition listed
// in `docs/design/fleet-redesign/brief.md`. `send` (#6770) is the prototype's `CMIC.send`
// (`:1843`): an arrow into a bar, not the plus the Send buttons carry.

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': '1.6',
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
} as const;

const GLYPH: Readonly<Record<CardMenuId, () => JSX.Element>> = {
  details: () => (
    <g {...STROKE}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="2" />
      <path d="M9.5 2.5v11" />
    </g>
  ),
  send: () => (
    <g {...STROKE}>
      <path d="M2.5 8h9M8 3.5 12.5 8 8 12.5" />
      <path d="M14 3v10" opacity="0.5" />
    </g>
  ),
  // #6772: the prototype's `CMIC.move` and `CMIC.drain` (`:1842-1856`), verbatim.
  move_work: () => (
    <path {...STROKE} d="M3 5h9.5M10 2.5 12.5 5 10 7.5M13 11H3.5M6 8.5 3.5 11 6 13.5" />
  ),
  drain_select: () => (
    <g {...STROKE}>
      <rect x="2.5" y="2.5" width="11" height="6" rx="1.5" />
      <path d="M8 8.5v5M5.5 11 8 13.5 10.5 11" />
    </g>
  ),
  drain: () => (
    <g fill="currentColor">
      <rect x="4" y="3" width="2.6" height="10" rx="0.8" />
      <rect x="9.4" y="3" width="2.6" height="10" rx="0.8" />
    </g>
  ),
  pause_jobs: () => (
    <g fill="currentColor">
      <rect x="4" y="3" width="2.6" height="10" rx="0.8" />
      <rect x="9.4" y="3" width="2.6" height="10" rx="0.8" />
    </g>
  ),
  resume_jobs: () => (
    <path
      fill="currentColor"
      d="M5 3.2v9.6a.6.6 0 0 0 .9.5l7.6-4.8a.6.6 0 0 0 0-1L5.9 2.7a.6.6 0 0 0-.9.5z"
    />
  ),
  undrain: () => (
    <path
      fill="currentColor"
      d="M5 3.2v9.6a.6.6 0 0 0 .9.5l7.6-4.8a.6.6 0 0 0 0-1L5.9 2.7a.6.6 0 0 0-.9.5z"
    />
  ),
  simplify: () => <path {...STROKE} d="M4.5 2.5 8 6l3.5-3.5M4.5 13.5 8 10l3.5 3.5" />,
  expand: () => <path {...STROKE} d="M4.5 6 8 2.5 11.5 6M4.5 10 8 13.5l3.5-3.5" />,
  agents: () => (
    <g {...STROKE}>
      <circle cx="6" cy="5.5" r="2.5" />
      <path d="M1.8 13.5c.6-2.3 2.2-3.5 4.2-3.5s3.6 1.2 4.2 3.5" />
      <path d="M10.5 3.2a2.4 2.4 0 0 1 0 4.6M12 10.3c1.1.5 1.8 1.6 2.2 3.2" />
    </g>
  ),
  copy_id: () => (
    <g {...STROKE}>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.6" />
      <path d="M10.5 5.5V3.9c0-.8-.6-1.4-1.4-1.4H3.9c-.8 0-1.4.6-1.4 1.4v5.2c0 .8.6 1.4 1.4 1.4h1.6" />
    </g>
  ),
  rename: () => <path {...STROKE} d="M10.8 2.7l2.5 2.5L6 12.5l-3.3.8.8-3.3z" />,
  jobs_at_once: () => (
    <g {...STROKE}>
      <rect x="2.5" y="2.5" width="11" height="3" rx="1" />
      <rect x="2.5" y="6.5" width="11" height="3" rx="1" />
      <rect x="2.5" y="10.5" width="11" height="3" rx="1" />
    </g>
  ),
  instructions: () => (
    <g {...STROKE}>
      <path d="M4 2.5h6l2.5 2.5v8.5H4z" />
      <path d="M6.5 8h4M6.5 10.5h3" />
    </g>
  ),
  remove: () => <path {...STROKE} d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5" />,
};

export function MenuIcon(props: { readonly id: CardMenuId }): JSX.Element {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
      {GLYPH[props.id]()}
    </svg>
  );
}
